import { Input, InputRef, Tooltip } from 'antd';
import Form, { FormItemProps } from 'antd/lib/form';
import { InputProps } from 'antd/lib/input';
import { ChangeEvent, ForwardedRef, forwardRef, JSX } from 'react';
import { useDebounce } from 'use-debounce';
import colors from '../../../theme/colors';
import { isApiCryptOrMssEmail, validateEmail } from '../../../utils/email';

export interface EmailInputProps {
  disabled?: boolean;
  email: string;
  errorMessage?: string;
  placeholder?: string;
  setEmail?: (value: string) => void;
  validator?: (email: string) => boolean;
  formItemProps?: FormItemProps;
  isDematRecipient?: boolean;
  dematErrorMesage?: string;
  disabledTooltip?: string;
}

export const EmailInput = forwardRef(
  (
    {
      disabled = false,
      email,
      errorMessage,
      placeholder,
      setEmail,
      validator = validateEmail,
      formItemProps,
      isDematRecipient,
      dematErrorMesage,
      disabledTooltip,
      ...otherProps
    }: EmailInputProps & InputProps,
    ref: ForwardedRef<InputRef>,
  ): JSX.Element => {
    const [debouncedEmail] = useDebounce<string>(email, 1000);

    let validateStatus: 'success' | 'error' | undefined;
    let help;
    if (debouncedEmail.length > 0 && !disabled) {
      if (validator?.(debouncedEmail)) {
        if (isDematRecipient && dematErrorMesage && isApiCryptOrMssEmail(email)) {
          validateStatus = 'error';
          help = dematErrorMesage;
        } else {
          validateStatus = 'success';
        }
      } else {
        validateStatus = 'error';
        help = errorMessage;
      }
    }

    const onChange = setEmail
      ? (e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)
      : undefined;

    return (
      <Form.Item
        hasFeedback
        validateStatus={formItemProps?.validateStatus ?? validateStatus}
        help={formItemProps?.help ?? help}
        htmlFor="email"
        labelCol={{ span: 24 }}
        {...formItemProps}
      >
        <Tooltip
          title={disabled ? disabledTooltip : undefined}
          placement="topRight"
          color={colors.grey[9]}
        >
          <Input
            onChange={onChange}
            placeholder={placeholder}
            value={email}
            disabled={disabled}
            id="email"
            name="email"
            ref={ref}
            {...otherProps}
          />
        </Tooltip>
      </Form.Item>
    );
  },
);
